import { Card, CardHeader, Typography, CardContent, makeStyles, Box, Button } from '@material-ui/core'
import Grid, { GridSpacing } from '@material-ui/core/Grid'
import { Theme } from '@material-ui/core/styles/createTheme'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import classNames from 'classnames'
import upperFirst from 'lodash/upperFirst'
import React, { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import api from 'src/api'
import { LightPolicy, DomainPermission_DomainEnum, DomainPermission_VerbEnum, PenaltyScheme } from 'src/api/gen/api'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import useFeeRates from 'src/commons/hooks/useFeeRates'
import { userHasPermission, Can } from 'src/commons/permissions'
import { zonedDateTimeToISOString } from 'src/commons/temporalHelpers'
import { PublishButton } from 'src/components/Button'
import { CardFooter } from 'src/components/Card'
import { IDateTimeRange } from 'src/components/DatePickers'
import FormValidator from 'src/components/FormValidator'
import ValidationResults from 'src/components/FormValidator/ValidationResults'
import { useJoyrideWrapper } from 'src/components/Joyride'
import { Separator } from 'src/components/Separator'
import ErrorSnackbar from 'src/components/Snackbar'
import { useProviders } from 'src/layout/hooks'
import theme from 'src/theme'

import { PeriodField, PoliciesField, ProviderField } from './fields'

const useStyles = makeStyles((theme: Theme) => ({
  cardLoading: {
    padding: theme.spacing(80),
  },
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    borderRadius: 'inherit',
  },
  rootCard: {
    borderRadius: 'inherit',
    width: '100%',
    boxShadow: theme.shadows[1],
  },
  cardSection: {
    display: 'flex',
    flexWrap: 'wrap',
    paddingLeft: '30px',
    marginBottom: '30px',
  },
  cardHeaderTitle: {
    color: theme.palette.primary.main,
    margin: '15px 0px 15px 15px',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '15px 0px 15px',
  },
  sectionHeader: {
    width: '100%',
    marginBottom: '-6px',
  },
  buttonBar: { paddingRight: '32px', paddingLeft: '32px', textAlign: 'end' },
  closeNotifButton: {
    padding: theme.spacing(1 / 2),
  },
}))

export const formRules = {
  providers: {
    method: (value: string[]) => !!value.length,
    message: <Trans>messages.providers</Trans>,
  },
  periodFrom: {
    source: 'startDate',
    method: (value: Temporal.ZonedDateTime | undefined) => !!value,
    message: <Trans>messages.startDate</Trans>,
  },
  periodTo: {
    source: 'endDate',
    method: (value: Temporal.ZonedDateTime | undefined) => !!value,
    message: <Trans>messages.endDate</Trans>,
  },
  policies: {
    method: (value: string[]) => !!value.length,
    message: <Trans>messages.policies</Trans>,
  },
}
const validator = new FormValidator<typeof formRules>(formRules)

const IPenaltySchemeToCreatePenaltyScheme = (penaltyScheme: IPenaltyScheme) => ({
  start_date: zonedDateTimeToISOString(penaltyScheme.startDate!),
  end_date: zonedDateTimeToISOString(penaltyScheme.endDate!),
  policies: penaltyScheme.policies,
  providers: penaltyScheme.providers,
  is_fee_rates: true,
})

interface ICardSection {
  button?: React.ReactNode
  children?: React.ReactNode
  itemWrapping?: boolean
  lg?: 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  md?: 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  sm?: 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  spacing?: GridSpacing
  title?: string
  xl?: 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  xs?: 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
}
export const CardSection = function (props: ICardSection) {
  const classes = useStyles()
  const { spacing = 4, itemWrapping = true, xs, sm, md, lg, xl } = props

  const breakpoints = { xs, sm, md, lg, xl }

  return (
    <CardContent className={classes.cardSection}>
      <Grid container spacing={spacing}>
        <Grid item xs={12} style={{ display: 'flex' }}>
          <Typography className={classNames(classes.cardHeaderTitle, classes.sectionHeader)} variant="h5">
            {props.title}
          </Typography>
          {props.button}
        </Grid>
        {itemWrapping
          ? React.Children.map(props.children, (child) => (
              <Grid item {...breakpoints}>
                {child}
              </Grid>
            ))
          : props.children}
      </Grid>
    </CardContent>
  )
}

interface IPenaltyScheme {
  endDate: Temporal.ZonedDateTime | undefined
  policies: string[]
  providers: string[]
  startDate: Temporal.ZonedDateTime | undefined
}

interface IPenaltySchemeEditorProps {
  setPenaltyScheme: React.Dispatch<React.SetStateAction<PenaltyScheme | null | undefined>>
}

const PenaltySchemeEditor = (props: IPenaltySchemeEditorProps) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()
  const policyKinds = useFeeRates()
  const { activeProviders } = useProviders()

  const { run } = useJoyrideWrapper()

  const canWrite = userHasPermission([DomainPermission_VerbEnum.write, DomainPermission_DomainEnum.policy])

  const [notificationOpened, setNotificationOpened] = useState<boolean>(false)
  const [notificationMessage, setNotificationMessage] = useState<string>('')

  // Wording context
  const [penaltyScheme, setPenaltyScheme] = useState<IPenaltyScheme>({
    startDate: undefined,
    endDate: undefined,
    policies: [],
    providers: [],
  })

  const [validation, setValidation] = useState<ValidationResults<typeof formRules>>(validator.makeValid())

  const [allPolicies, setAllPolicies] = useState<LightPolicy[]>([])
  const [policiesProviders, setPoliciesProviders] = useState<string[]>([])

  async function fetchPolicies() {
    const response = await api.policies.policiesListLight({ kind: policyKinds.map((kind) => kind.id) })
    const policies = response.results
    setAllPolicies(policies)
    setPoliciesProviders(policies.flatMap((policy) => policy.providers ?? []))
  }

  useDeepCompareEffect(() => {
    fetchPolicies()
  }, [policyKinds])

  async function validateAndCreate() {
    const formValidation = validator.validate(penaltyScheme)
    setValidation(formValidation)

    if (!formValidation.isFormValid()) {
      setNotificationMessage(t('penaltyScheme.missingFields'))
      setNotificationOpened(true)
      return
    }
    try {
      setPenaltyScheme({ ...penaltyScheme })
      const createdPenaltyScheme = await api.penaltySchemes.penaltySchemesCreate({
        data: IPenaltySchemeToCreatePenaltyScheme(penaltyScheme),
      })
      props.setPenaltyScheme(createdPenaltyScheme)
    } catch (e) {
      setNotificationMessage(t('penaltyScheme.serverError'))
      setNotificationOpened(true)
    }
  }

  const handleProvidersChange = function (providers: string[]) {
    setPenaltyScheme({ ...penaltyScheme, providers })
  }

  function handlePoliciesChange(policies: string[]) {
    setPenaltyScheme({ ...penaltyScheme, policies })
  }

  function handleDateRangeChange(dateRange: IDateTimeRange) {
    penaltyScheme.startDate = dateRange.from
    penaltyScheme.endDate = dateRange.to
    setPenaltyScheme({
      ...penaltyScheme,
    })
  }

  return (
    <div className={classes.root}>
      <Card className={classes.rootCard}>
        <CardHeader
          title={
            <Box display="flex">
              <Box display="flex" flexGrow={1}>
                <Typography className={classes.cardHeaderTitle} variant="h2">
                  {upperFirst(t('titles.penalty_scheme.editor.fees'))}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                <Button variant="outlined" color="primary" startIcon={<HelpOutlineIcon />} onClick={() => run()}>
                  {t('onboarding.ui.start_button')}
                </Button>
              </Box>
            </Box>
          }
        />
        <Separator />
        <CardSection xs={4}>
          <ProviderField
            id="fee_scheme_provider"
            title={t('titles.providers')}
            providers={activeProviders.filter(
              (provider) => policiesProviders.find((policyProvider) => policyProvider === provider.id) !== undefined
            )}
            value={penaltyScheme.providers}
            handleChange={handleProvidersChange}
            validation={validation}
          />
          <PoliciesField
            id="fee_scheme_fee_rates"
            title={t('titles.fee_rates')}
            value={penaltyScheme.policies}
            allPolicies={allPolicies}
            handleChange={handlePoliciesChange}
            validation={validation}
          />
          <PeriodField
            id="fee_scheme_time_range"
            title={t('titles.time_range')}
            value={{ from: penaltyScheme.startDate, to: penaltyScheme.endDate }}
            handleChange={handleDateRangeChange}
            validation={validation}
          />
        </CardSection>
        <Can I={DomainPermission_VerbEnum.write} a={DomainPermission_DomainEnum.policy}>
          <Separator />
          <CardFooter className={classes.cardFooter}>
            <div className={classes.buttonBar}>
              {canWrite ? <PublishButton label={t('titles.generate')} onClick={() => validateAndCreate()} /> : null}
            </div>
          </CardFooter>
        </Can>
      </Card>

      <ErrorSnackbar
        open={notificationOpened}
        onClose={() => setNotificationOpened(false)}
        message={notificationMessage}
      />
    </div>
  )
}

export default PenaltySchemeEditor
